import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { renderPdfPageToDataUrl } from "@/lib/pdf";
import { importSurveyFile } from "@/lib/kml";
import { deliverJobPdf } from "@/lib/pdfExport";
import { PinDetailsDialog } from "@/components/PinDetailsDialog";
import { buildJobKml } from "@/lib/kmlExport";
import { useAuth } from "@/context/AuthContext";
import Attachments from "@/components/Attachments";
import SignaturePad from "@/components/SignaturePad";
import { toast } from "sonner";
import { UTILITIES, getUtil } from "@/constants/utilities";
import MapView from "@/components/MapView";
import SketchCanvas from "@/components/SketchCanvas";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ArrowLeft, MapPin, PencilRuler, Sparkles, Save, Pin, Route, Locate, Image as ImageIcon, Pencil, Minus, Square, Circle as CircleIcon, MoveUpRight, Type, Ruler, Globe2, Mail, FileDown, PenLine, CheckCircle2, Spline, PackageX, Hash } from "lucide-react";

const useDebouncedSave = () => {}; // legacy helper — replaced by inline save logic in JobEditor

const JobEditor = () => {
  const { jobId } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const jobRef = useRef(null); // always holds the latest job state (for flush-on-unmount)
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("map"); // map | sketch
  const [activeService, setActiveService] = useState("water");
  const [mapMode, setMapMode] = useState("pin"); // pin | line | view
  const [sketchTool, setSketchTool] = useState("pen"); // pen | line | rect | ellipse | arrow | text | depth
  const [brushSize, setBrushSize] = useState(6);
  const [bgImage, setBgImage] = useState(null); // dataURL for sketch background
  const [pdfBuffer, setPdfBuffer] = useState(null); // ArrayBuffer for multi-page PDF
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPages, setPdfPages] = useState(0);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [savingAt, setSavingAt] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [aiExtra, setAiExtra] = useState("");
  const [aiError, setAiError] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailIncludeDxf, setEmailIncludeDxf] = useState(false);
  // Selected pin for the details dialog. `null` = closed.
  const [selectedPinId, setSelectedPinId] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [pdfBusy2, setPdfBusy2] = useState(false);
  const { user } = useAuth();
  const dirtyRef = useRef(false);
  const saveTimerRef = useRef(null);
  const inflightSaveRef = useRef(null);

  // Keep a live ref to the current job so flush-on-unmount can read it.
  useEffect(() => { jobRef.current = job; }, [job]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/jobs/${jobId}`);
        setJob(r.data);
        setAiSummary(r.data.ai_summary || "");
      } catch { toast.error("Job not found"); nav("/jobs"); }
      finally { setLoading(false); }
    })();
  }, [jobId, nav]);

  // Persist the current job to the server. Returns the promise.
  const saveNow = React.useCallback(async () => {
    const cur = jobRef.current;
    if (!cur || !dirtyRef.current) return;
    // Clear any pending debounce so it can't double-fire while we're saving.
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    // Snapshot the intent — mark clean *before* the network call so subsequent
    // edits made during the request still get flagged dirty for the next save.
    dirtyRef.current = false;
    const payload = {
      pins: cur.pins, lines: cur.lines, strokes: cur.strokes,
      name: cur.name, address: cur.address, notes: cur.notes,
      center_lat: cur.center_lat, center_lng: cur.center_lng,
    };
    inflightSaveRef.current = api.patch(`/jobs/${jobId}`, payload)
      .then(() => setSavingAt(new Date()))
      .catch(() => { dirtyRef.current = true; toast.error("Auto-save failed"); })
      .finally(() => { inflightSaveRef.current = null; });
    return inflightSaveRef.current;
  }, [jobId]);

  // Debounced auto-save: schedules a save 900 ms after the last mutation.
  useEffect(() => {
    if (!job) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { saveNow(); }, 900);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [job, saveNow]);

  // Flush pending edits when leaving/closing the tab (uses fetch keepalive).
  useEffect(() => {
    const flushWithBeacon = () => {
      const cur = jobRef.current;
      if (!cur || !dirtyRef.current) return;
      const url = `${api.defaults.baseURL || ""}/jobs/${jobId}`;
      const token = localStorage.getItem("fp_token") || "";
      const body = JSON.stringify({
        pins: cur.pins, lines: cur.lines, strokes: cur.strokes,
        name: cur.name, address: cur.address, notes: cur.notes,
        center_lat: cur.center_lat, center_lng: cur.center_lng,
      });
      try {
        fetch(url, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body, keepalive: true,
        });
        dirtyRef.current = false;
      } catch (_) { /* best-effort */ }
    };
    const onVis = () => { if (document.visibilityState === "hidden") flushWithBeacon(); };
    window.addEventListener("beforeunload", flushWithBeacon);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("beforeunload", flushWithBeacon);
      document.removeEventListener("visibilitychange", onVis);
      // Also flush on component unmount (e.g. user navigates to /jobs)
      flushWithBeacon();
    };
  }, [jobId]);

  const patch = (partial) => {
    dirtyRef.current = true;
    setJob(j => ({ ...j, ...partial }));
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        patch({ center_lat: pos.coords.latitude, center_lng: pos.coords.longitude });
        toast.success("Centred on your location");
      },
      () => toast.error("Could not get location"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const onImportKml = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    try {
      const { pins: newPins, lines: newLines, bounds, kind } = await importSurveyFile(file, activeService);
      if (newPins.length === 0 && newLines.length === 0) {
        toast.error("No points or lines found in the file");
        return;
      }
      const update = {
        pins: [...(job.pins || []), ...newPins],
        lines: [...(job.lines || []), ...newLines],
      };
      if (bounds) {
        update.center_lat = (bounds[0][0] + bounds[1][0]) / 2;
        update.center_lng = (bounds[0][1] + bounds[1][1]) / 2;
      }
      patch(update);
      toast.success(
        `Imported ${newPins.length} pin${newPins.length !== 1 ? "s" : ""} and ` +
        `${newLines.length} line${newLines.length !== 1 ? "s" : ""} as ${activeService.toUpperCase()} (${kind.toUpperCase()})`
      );
    } catch (err) {
      toast.error(err?.message || "Could not read file — supported: KML, KMZ, GeoJSON, GPX, CSV");
    }
  };

  const runAI = async () => {
    setAiBusy(true);
    setAiError("");
    try {
      // Ensure the latest local edits (new pins/lines/sketches) are persisted
      // BEFORE we ask the backend to read the job and generate a report.
      await saveNow();
      if (inflightSaveRef.current) await inflightSaveRef.current;
      const r = await api.post(`/jobs/${jobId}/summarize`, { extra_notes: aiExtra });
      setAiSummary(r.data.summary);
      toast.success("Site report generated");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "AI service unavailable";
      setAiError(String(msg));
      toast.error("AI report failed — see dialog for details");
    } finally {
      setAiBusy(false);
    }
  };

  // Both MapView and SketchCanvas are permanently mounted in the DOM
  // (only their z-index/pointer-events change with the active tab), so
  // we can snapshot either surface at any time without state toggling.
  const capturePlanSurfaces = async () => {
    const { snapshotNode } = await import("@/lib/pdfExport");
    let sketchSnap = null, mapSnap = null, sketchAspect = null, mapAspect = null;
    const sketchEl = document.querySelector("#fieldplan-sketch-capture");
    if (sketchEl) {
      sketchAspect = sketchEl.offsetWidth / sketchEl.offsetHeight;
      try { sketchSnap = await snapshotNode("#fieldplan-sketch-capture"); }
      catch (_) { sketchSnap = null; }
    }
    const mapEl = document.querySelector("#fieldplan-map-capture");
    if (mapEl) {
      mapAspect = mapEl.offsetWidth / mapEl.offsetHeight;
      try { mapSnap = await snapshotNode("#fieldplan-map-capture"); }
      catch (_) { mapSnap = null; }
    }
    return { mapSnap, mapAspect, sketchSnap, sketchAspect };
  };

  const emailReport = async () => {
    if (!emailTo || !/^\S+@\S+\.\S+$/.test(emailTo)) {
      toast.error("Enter a valid client email");
      return;
    }
    setEmailBusy(true);
    try {
      await saveNow();
      if (inflightSaveRef.current) await inflightSaveRef.current;
      // Ensure the Sketch tab has mounted at least once, otherwise the
      // sketch capture snapshot won't render into the PDF.
      const { buildJobPdf } = await import("@/lib/pdfExport");
      // Toggle tabs to guarantee both surfaces are captured, then build.
      const surfaces = await capturePlanSurfaces();
      const { blob, filename } = await buildJobPdf(job, user, {
        planOnly: true,
        ...surfaces,
      });
      // Convert Blob → base64 for JSON transport.
      const b64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(",", 2)[1] || "");
        fr.onerror = () => reject(fr.error);
        fr.readAsDataURL(blob);
      });
      let dxfText = null;
      let dxfFilename = null;
      if (emailIncludeDxf) {
        const { buildJobDxf } = await import("@/lib/dxfExport");
        dxfText = buildJobDxf(job);
        const safeName = (job.name || "site").replace(/[^\w-]+/g, "_");
        const stamp = new Date().toISOString().slice(0, 10);
        dxfFilename = `field-plan_${safeName}_${stamp}.dxf`;
      }
      const r = await api.post(`/jobs/${jobId}/email-plan`, {
        recipient_email: emailTo,
        message: emailMessage || "",
        pdf_base64: b64,
        pdf_filename: filename,
        dxf_text: dxfText,
        dxf_filename: dxfFilename,
      });
      toast.success(`Plan emailed to ${r.data.sent_to}`);
      setEmailOpen(false);
      setEmailTo("");
      setEmailMessage("");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send email";
      toast.error(detail);
    } finally {
      setEmailBusy(false);
    }
  };

  const exportPdf = async () => {
    setPdfBusy2(true);
    try {
      await saveNow();
      if (inflightSaveRef.current) await inflightSaveRef.current;
      // Prime both tabs so the full report PDF always contains map + sketch,
      // regardless of which tab the tradie was on.
      const surfaces = await capturePlanSurfaces();
      const { buildJobPdf } = await import("@/lib/pdfExport");
      const { blob, filename } = await buildJobPdf(job, user, surfaces);
      // Deliver — mobile share sheet or classic download.
      const file = new File([blob], filename, { type: "application/pdf" });
      if (typeof navigator !== "undefined"
          && navigator.canShare && navigator.canShare({ files: [file] })
          && typeof navigator.share === "function") {
        try {
          await navigator.share({ files: [file], title: job.name || "Site plan" });
          toast.success("PDF ready — pick Save to Files");
        } catch (err) {
          if (err && err.name === "AbortError") { toast.info("PDF export cancelled"); }
          else { throw err; }
        }
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success("PDF downloaded");
      }
    } catch (err) {
      toast.error("Could not generate PDF");
    } finally {
      setPdfBusy2(false);
    }
  };

  const saveSignOff = async ({ dataUrl, name }) => {
    try {
      const signed_at = new Date().toISOString();
      const r = await api.patch(`/jobs/${jobId}`, {
        client_name: name,
        client_signature: dataUrl,
        signed_at,
      });
      setJob(r.data);
      dirtyRef.current = false;
      setSignOpen(false);
      toast.success(`Signed off by ${name}`);
    } catch (err) {
      toast.error("Could not save sign-off");
    }
  };

  const onUploadBg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // allow re-uploading the same file later

    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
    const isDxf = /\.dxf$/i.test(file.name);
    if (isDxf) {
      try {
        const { dxfFileToSvgDataUrl } = await import("@/lib/dxfImport");
        const dataUrl = await dxfFileToSvgDataUrl(file);
        setBgImage(dataUrl);
        setPdfBuffer(null); setPdfPages(0); setPdfPage(1);
        toast.success("CAD (DXF) loaded — trace over it on the sketch");
      } catch (err) {
        toast.error("Could not read DXF — is it a valid text-based AutoCAD file?");
      }
      return;
    }
    if (isPdf) {
      setPdfBusy(true);
      try {
        const buf = await file.arrayBuffer();
        const { dataUrl, numPages } = await renderPdfPageToDataUrl(buf, 1);
        setPdfBuffer(buf);
        setPdfPages(numPages);
        setPdfPage(1);
        setBgImage(dataUrl);
        toast.success(`PDF loaded — ${numPages} page${numPages > 1 ? "s" : ""}`);
      } catch (err) {
        toast.error("Could not render PDF");
      } finally {
        setPdfBusy(false);
      }
      return;
    }

    // Image path
    const reader = new FileReader();
    reader.onload = () => {
      setBgImage(reader.result);
      setPdfBuffer(null); setPdfPages(0); setPdfPage(1);
    };
    reader.readAsDataURL(file);
  };

  const changePdfPage = async (delta) => {
    if (!pdfBuffer || pdfPages <= 1) return;
    const next = Math.min(Math.max(pdfPage + delta, 1), pdfPages);
    if (next === pdfPage) return;
    setPdfBusy(true);
    try {
      const { dataUrl } = await renderPdfPageToDataUrl(pdfBuffer, next);
      setPdfPage(next);
      setBgImage(dataUrl);
    } catch (err) {
      toast.error("Could not switch page");
    } finally {
      setPdfBusy(false);
    }
  };

  const clearBg = () => {
    setBgImage(null); setPdfBuffer(null); setPdfPages(0); setPdfPage(1);
  };

  if (loading || !job) {
    return <div className="p-10 font-mono text-sm">Loading job…</div>;
  }

  const pinCount = (job.pins || []).length;
  const lineCount = (job.lines || []).length;
  const strokeCount = (job.strokes || []).length;

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <header className="flex items-center justify-between gap-3 px-4 md:px-6 py-3 border-b-2 border-foreground bg-background z-30">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="back-to-jobs" onClick={async () => { await saveNow(); nav("/jobs"); }} className="p-2 border-2 border-foreground hover:bg-foreground hover:text-background transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Job</div>
            <div className="font-heading font-black uppercase text-xl md:text-2xl truncate">{job.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:block font-mono text-[11px] uppercase text-muted-foreground">
            {savingAt ? `Saved ${savingAt.toLocaleTimeString()}` : dirtyRef.current ? "Unsaved…" : "Synced"}
          </div>
          {/* Trial countdown pill — visible only while trialing or expired,
              so paying subscribers get a clean header. Clickable → /billing. */}
          {(() => {
            const status = user?.subscription_status;
            const trialEnds = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
            const now = Date.now();
            const isTrialing = status === "trialing" && trialEnds && trialEnds.getTime() > now;
            const isExpired = status === "trialing" && trialEnds && trialEnds.getTime() <= now;
            if (!isTrialing && !isExpired) return null;
            const daysLeft = isTrialing
              ? Math.max(0, Math.ceil((trialEnds.getTime() - now) / (1000 * 60 * 60 * 24)))
              : 0;
            const cls = isTrialing
              ? "bg-[#FFC72C] text-foreground"
              : "bg-destructive text-background";
            return (
              <Link
                to="/billing"
                data-testid="header-trial-pill"
                className={`inline-flex items-center gap-1.5 border-2 border-foreground px-2 py-1 font-heading font-black uppercase text-[10px] tracking-widest ${cls} hover:shadow-block-sm transition-shadow`}
                title={isTrialing ? `${daysLeft} days left in trial — click to subscribe` : "Trial ended — click to subscribe"}
              >
                {isTrialing ? `Trial · ${daysLeft}d left` : "Trial ended · Subscribe"}
              </Link>
            );
          })()}
          {job?.client_signature && (
            <div data-testid="signed-badge" className="hidden md:inline-flex items-center gap-1.5 border-2 border-foreground bg-background px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
              <CheckCircle2 className="w-3 h-3" /> Signed
            </div>
          )}
          <button data-testid="btn-signoff" onClick={() => setSignOpen(true)}
            className="btn-ghost-rugged text-xs inline-flex items-center gap-1.5">
            <PenLine className="w-4 h-4" />
            <span className="hidden md:inline">{job?.client_signature ? "Re-sign" : "Sign-off"}</span>
          </button>
          <button data-testid="btn-export-pdf" onClick={exportPdf} disabled={pdfBusy2}
            className="btn-ghost-rugged text-xs inline-flex items-center gap-1.5">
            <FileDown className="w-4 h-4" />
            <span className="hidden md:inline">{pdfBusy2 ? "Building…" : "Export PDF"}</span>
          </button>
          <button data-testid="btn-export-kml"
            onClick={async () => { await saveNow(); buildJobKml(job); toast.success("KML downloaded — open in Google Earth"); }}
            className="btn-ghost-rugged text-xs inline-flex items-center gap-1.5">
            <FileDown className="w-4 h-4" />
            <span className="hidden md:inline">Export KML</span>
          </button>
          <button data-testid="btn-export-dxf"
            onClick={async () => {
              await saveNow();
              const { deliverJobDxf } = await import("@/lib/dxfExport");
              const res = await deliverJobDxf(job);
              if (res === "shared") toast.success("CAD file ready — pick Save to Files");
              else if (res === "downloaded") toast.success("CAD (DXF) downloaded — opens in AutoCAD/LibreCAD");
            }}
            title="Export as AutoCAD DXF (pins, lines & sketches on separate layers)"
            className="btn-ghost-rugged text-xs inline-flex items-center gap-1.5">
            <FileDown className="w-4 h-4" />
            <span className="hidden md:inline">Export CAD</span>
          </button>
          <button data-testid="btn-save-backup"
            onClick={async () => {
              await saveNow();
              const { exportJobBackup } = await import("@/lib/jobBackup");
              const res = await exportJobBackup(job);
              if (res === "shared") toast.success("Backup ready — pick Save to Files");
              else if (res === "downloaded") toast.success("Backup file downloaded");
            }}
            title="Save the full job as a .fpjob.json file (pins, lines, sketches, AI summary) — re-import later"
            className="btn-ghost-rugged text-xs inline-flex items-center gap-1.5">
            <Save className="w-4 h-4" />
            <span className="hidden md:inline">Save backup</span>
          </button>
          <button data-testid="btn-email-plan"
            onClick={() => setEmailOpen(true)}
            title="Email the completed plan (map + sketch + AI + sign-off PDF) to your client"
            className="btn-ghost-rugged text-xs inline-flex items-center gap-1.5">
            <Mail className="w-4 h-4" />
            <span className="hidden md:inline">Email plan</span>
          </button>
          <button data-testid="btn-ai-report" onClick={() => setAiOpen(true)} className="btn-rugged text-xs inline-flex items-center gap-1.5">
            <Sparkles className="w-4 h-4" /> AI Report
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b-2 border-foreground bg-background flex">
        <button data-testid="tab-map" onClick={() => setTab("map")}
          className={`flex-1 md:flex-none px-5 py-3 font-heading font-black uppercase text-sm tracking-widest border-r-2 border-foreground inline-flex items-center gap-2 ${tab === "map" ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}>
          <MapPin className="w-4 h-4" /> Map
        </button>
        <button data-testid="tab-sketch" onClick={() => setTab("sketch")}
          className={`flex-1 md:flex-none px-5 py-3 font-heading font-black uppercase text-sm tracking-widest border-r-2 border-foreground inline-flex items-center gap-2 ${tab === "sketch" ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}>
          <PencilRuler className="w-4 h-4" /> Sketch
        </button>
        <div className="hidden md:flex items-center gap-3 px-5 ml-auto text-xs font-mono uppercase text-muted-foreground">
          <span>{pinCount} pins</span>
          <span>·</span>
          <span>{lineCount} runs</span>
          <span>·</span>
          <span>{strokeCount} strokes</span>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex min-h-0">
        {/* Left palette */}
        <aside className="w-[86px] md:w-[220px] border-r-2 border-foreground bg-background overflow-y-auto shrink-0">
          <div className="p-3 border-b-2 border-foreground">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Service</div>
          </div>
          <div className="p-2 grid grid-cols-1 gap-1.5">
            {UTILITIES.map(u => (
              <button key={u.key} data-testid={`svc-${u.key}`} onClick={() => setActiveService(u.key)}
                className={`flex items-center gap-2 px-2 py-2 border-2 transition-colors text-left ${activeService === u.key ? "border-foreground bg-foreground text-background" : "border-foreground bg-background hover:bg-muted"}`}>
                <span className="w-4 h-4 border-2 border-foreground shrink-0" style={{ background: u.color }} />
                <span className="font-heading font-bold uppercase text-[11px] tracking-wider hidden md:inline">{u.label}</span>
              </button>
            ))}
          </div>

          {tab === "map" && (
            <div className="p-3 border-t-2 border-foreground space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Tool</div>
              <button data-testid="mode-pin" onClick={() => setMapMode("pin")}
                className={`w-full flex items-center gap-2 px-2 py-2 border-2 border-foreground text-left ${mapMode === "pin" ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}>
                <Pin className="w-4 h-4 shrink-0" /><span className="font-heading font-bold uppercase text-[11px] tracking-wider hidden md:inline">Drop pin</span>
              </button>
              <button data-testid="mode-line" onClick={() => setMapMode("line")}
                className={`w-full flex items-center gap-2 px-2 py-2 border-2 border-foreground text-left ${mapMode === "line" ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}>
                <Route className="w-4 h-4 shrink-0" /><span className="font-heading font-bold uppercase text-[11px] tracking-wider hidden md:inline">Draw run</span>
              </button>
              <button data-testid="mode-view" onClick={() => setMapMode("view")}
                className={`w-full flex items-center gap-2 px-2 py-2 border-2 border-foreground text-left ${mapMode === "view" ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}>
                <MapPin className="w-4 h-4 shrink-0" /><span className="font-heading font-bold uppercase text-[11px] tracking-wider hidden md:inline">Pan only</span>
              </button>

              <button data-testid="use-my-location" onClick={useMyLocation} className="w-full btn-ghost-rugged text-[11px] py-2 mt-2 inline-flex items-center gap-2">
                <Locate className="w-4 h-4" /><span className="hidden md:inline">Use my GPS</span>
              </button>

              <label className="w-full btn-ghost-rugged text-[11px] py-2 inline-flex items-center justify-center gap-2 cursor-pointer">
                <Globe2 className="w-4 h-4" />
                <span className="hidden md:inline">Import survey file</span>
                <input data-testid="kml-upload" type="file"
                  accept=".kml,.kmz,.geojson,.json,.gpx,.csv,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz,application/geo+json,application/gpx+xml,text/csv"
                  onChange={onImportKml} className="hidden" />
              </label>
              <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground text-center leading-relaxed hidden md:block">
                KML · KMZ · GeoJSON · GPX · CSV
              </div>
            </div>
          )}

          {tab === "sketch" && (
            <div className="p-3 border-t-2 border-foreground space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Tool</div>
              {[
                { key: "pen",      label: "Freehand", Icon: Pencil },
                { key: "line",     label: "Line",     Icon: Minus },
                { key: "curve",    label: "Curve",    Icon: Spline },
                { key: "arrow",    label: "Arrow",    Icon: MoveUpRight },
                { key: "rect",     label: "Rect",     Icon: Square },
                { key: "boxcross", label: "Pit box",  Icon: PackageX },
                { key: "ellipse",  label: "Circle",   Icon: CircleIcon },
                { key: "symbol",   label: "AS symbol",Icon: Hash },
                { key: "text",     label: "Label",    Icon: Type },
                { key: "depth",    label: "Depth",    Icon: Ruler },
              ].map(({ key, label, Icon }) => (
                <button
                  key={key}
                  data-testid={`tool-${key}`}
                  onClick={() => setSketchTool(key)}
                  className={`w-full flex items-center gap-2 px-2 py-2 border-2 border-foreground text-left ${sketchTool === key ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="font-heading font-bold uppercase text-[11px] tracking-wider hidden md:inline">{label}</span>
                </button>
              ))}

              <div className="pt-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Brush</div>
              <input data-testid="brush-size" type="range" min="2" max="24" value={brushSize}
                onChange={e => setBrushSize(Number(e.target.value))}
                className="w-full accent-foreground" />
              <div className="font-mono text-xs">{brushSize}px</div>

              <label className="w-full btn-ghost-rugged text-[11px] py-2 mt-2 inline-flex items-center justify-center gap-2 cursor-pointer">
                <ImageIcon className="w-4 h-4" />
                <span className="hidden md:inline">{pdfBusy ? "Loading…" : "Upload photo / PDF / DXF"}</span>
                <input data-testid="bg-upload" type="file" accept="image/*,application/pdf,.pdf,.dxf" onChange={onUploadBg} className="hidden" />
              </label>
              <button
                type="button"
                data-testid="btn-dropbox-import"
                onClick={async () => {
                  const { pickDropboxCadFile, isDropboxConfigured } = await import("@/lib/dropboxChooser");
                  if (!isDropboxConfigured()) {
                    toast.error("Dropbox import isn't set up yet — ask the admin to add the Dropbox App key.");
                    return;
                  }
                  pickDropboxCadFile({
                    onFile: async ({ name, text }) => {
                      try {
                        const { dxfTextToSvgDataUrl } = await import("@/lib/dxfImport");
                        const dataUrl = dxfTextToSvgDataUrl(text);
                        setBgImage(dataUrl);
                        setPdfBuffer(null); setPdfPages(0); setPdfPage(1);
                        toast.success(`Loaded ${name} from Dropbox — trace on the sketch`);
                      } catch (err) {
                        toast.error("Could not parse the DXF from Dropbox.");
                      }
                    },
                    onError: (err) => toast.error(err?.message || "Dropbox import failed"),
                  });
                }}
                className="w-full btn-ghost-rugged text-[11px] py-2 mt-2 inline-flex items-center justify-center gap-2"
              >
                <ImageIcon className="w-4 h-4" />
                <span className="hidden md:inline">From Dropbox (DXF)</span>
              </button>

              {pdfPages > 1 && (
                <div className="flex items-center gap-1 mt-1">
                  <button data-testid="pdf-prev" onClick={() => changePdfPage(-1)} disabled={pdfBusy || pdfPage <= 1}
                    className="btn-ghost-rugged text-[11px] py-1.5 px-2 flex-1 disabled:opacity-40">Prev</button>
                  <span className="font-mono text-[10px] uppercase tracking-widest px-1 hidden md:inline">
                    {pdfPage}/{pdfPages}
                  </span>
                  <button data-testid="pdf-next" onClick={() => changePdfPage(1)} disabled={pdfBusy || pdfPage >= pdfPages}
                    className="btn-ghost-rugged text-[11px] py-1.5 px-2 flex-1 disabled:opacity-40">Next</button>
                </div>
              )}

              {bgImage && (
                <button data-testid="bg-clear" onClick={clearBg} className="w-full btn-ghost-rugged text-[11px] py-2">
                  <span className="hidden md:inline">Remove background</span><span className="md:hidden">X</span>
                </button>
              )}
            </div>
          )}

          <Attachments jobId={jobId} />
        </aside>

        {/* Canvas / Map area. Both surfaces stay mounted so PDF/email
            snapshots never depend on a fragile tab-flash. Only the
            inactive surface is hidden from pointer input + view. */}
        <main className="flex-1 relative min-w-0">
          {/* Both surfaces stay mounted & full-size so the active one fully
              covers the other, but html-to-image can still capture either
              at any moment. Inactive layer is z-below and non-interactive. */}
          <div
            className={`absolute inset-0 ${tab === "map" ? "z-10" : "z-0 pointer-events-none"}`}
            aria-hidden={tab !== "map"}
          >
            <MapView job={job} activeService={activeService} mode={mapMode} onChange={patch}
              onPinClick={(p) => setSelectedPinId(p.id)} />
          </div>
          <div
            className={`absolute inset-0 ${tab === "sketch" ? "z-10" : "z-0 pointer-events-none"}`}
            aria-hidden={tab !== "sketch"}
          >
            <SketchCanvas job={job} activeService={activeService} brushSize={brushSize} tool={sketchTool} onChange={patch} backgroundUrl={bgImage} />
          </div>

          {/* Active service badge */}
          <div className="absolute bottom-3 right-3 z-[1000] bg-background border-2 border-foreground px-3 py-1.5 flex items-center gap-2 shadow-block-sm">
            <span className="w-3 h-3 border-2 border-foreground" style={{ background: getUtil(activeService).color }} />
            <span className="font-heading font-black uppercase text-[11px] tracking-widest">{getUtil(activeService).label}</span>
            <span className="font-mono text-[10px] uppercase text-muted-foreground">
              / {tab === "map" ? mapMode : sketchTool}
            </span>
          </div>
        </main>
      </div>

      {/* AI Dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="border-2 border-foreground rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-2xl flex items-center gap-2">
              <Sparkles className="w-5 h-5" /> AI Site Report
            </DialogTitle>
            <DialogDescription>Turn your pins, runs and notes into a plain-English report.</DialogDescription>
          </DialogHeader>

          <div>
            <label className="font-heading font-bold uppercase text-xs tracking-widest">Extra notes for AI (optional)</label>
            <textarea data-testid="ai-extra" rows={3} value={aiExtra} onChange={e => setAiExtra(e.target.value)}
              placeholder="e.g. Client concerned about sewer clash near driveway"
              className="mt-1 w-full border-2 border-foreground bg-background px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>

          <div className="border-2 border-foreground bg-muted/40 p-4 min-h-[200px] max-h-[380px] overflow-auto whitespace-pre-wrap font-body text-sm">
            {aiBusy ? "Generating report…" : (aiSummary || "No report yet. Click Generate.")}
          </div>

          {aiError && (
            <div data-testid="ai-error" className="border-2 border-destructive bg-destructive/10 text-destructive px-3 py-2 font-mono text-xs">
              {aiError.includes("Budget") || aiError.includes("budget")
                ? "AI key budget exceeded. Please top up the Emergent Universal Key balance (Profile → Universal Key → Add Balance) and try again."
                : aiError}
            </div>
          )}

          <DialogFooter className="gap-2">
            <button data-testid="ai-generate" onClick={runAI} disabled={aiBusy} className="btn-rugged">
              {aiBusy ? "Working…" : (aiSummary ? "Regenerate" : "Generate")}
            </button>
            {aiSummary && (
              <button data-testid="ai-copy" onClick={() => { navigator.clipboard.writeText(aiSummary); toast.success("Copied"); }} className="btn-ghost-rugged">
                Copy
              </button>
            )}
            {aiSummary && (
              <button data-testid="ai-email" onClick={() => { setAiOpen(false); setEmailOpen(true); }} className="btn-ghost-rugged inline-flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email to client
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email plan dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="border-2 border-foreground rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-2xl flex items-center gap-2">
              <Mail className="w-5 h-5" /> Email plan
            </DialogTitle>
            <DialogDescription>
              Emails the complete PDF (map + sketch + AI summary + sign-off) as an attachment. Replies come back to you.
            </DialogDescription>
          </DialogHeader>

          <div>
            <label className="font-heading font-bold uppercase text-xs tracking-widest">Client email</label>
            <input data-testid="email-to" type="email" value={emailTo}
              onChange={e => setEmailTo(e.target.value)} placeholder="client@example.com"
              className="mt-1 w-full border-2 border-foreground bg-background px-3 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label className="font-heading font-bold uppercase text-xs tracking-widest">Message (optional)</label>
            <textarea data-testid="email-message" value={emailMessage} rows={3}
              onChange={e => setEmailMessage(e.target.value)} placeholder="e.g. Locate finished — see PDF for services & depths."
              className="mt-1 w-full border-2 border-foreground bg-background px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <label className="flex items-center gap-2 border-2 border-foreground bg-background p-3 cursor-pointer">
            <input
              data-testid="email-include-dxf"
              type="checkbox"
              checked={emailIncludeDxf}
              onChange={(e) => setEmailIncludeDxf(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="font-mono text-xs uppercase tracking-widest">
              Also attach CAD (DXF) file — opens in AutoCAD / LibreCAD
            </span>
          </label>

          <DialogFooter className="gap-2">
            <button data-testid="email-send" onClick={emailReport} disabled={emailBusy} className="btn-rugged inline-flex items-center gap-2">
              <Mail className="w-4 h-4" /> {emailBusy ? "Building & sending…" : "Send plan"}
            </button>
            <button data-testid="email-cancel" onClick={() => setEmailOpen(false)} className="btn-ghost-rugged">
              Cancel
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client sign-off dialog */}
      <SignaturePad
        open={signOpen}
        onOpenChange={setSignOpen}
        onSave={saveSignOff}
        initialSignature={job?.client_signature || ""}
        initialName={job?.client_name || ""}
      />

      {/* Pin details dialog — photo upload + gallery. Reads pin fresh from
          `job.pins` each render so state stays in sync with autosave. */}
      <PinDetailsDialog
        open={!!selectedPinId}
        onOpenChange={(o) => { if (!o) setSelectedPinId(null); }}
        jobId={jobId}
        pin={(job?.pins || []).find((p) => p.id === selectedPinId) || null}
        onUpdatePin={(updated) => {
          const nextPins = (job?.pins || []).map((p) => (p.id === updated.id ? updated : p));
          patch({ pins: nextPins });
        }}
        onRemovePin={(id) => {
          const nextPins = (job?.pins || []).filter((p) => p.id !== id);
          patch({ pins: nextPins });
        }}
      />
    </div>
  );
};

export default JobEditor;
